import { Spx, Style } from '@wix/thunderbolt-becky-types'
import { StylableDriver, StylableEditor, StylesheetDriver } from '@wix/stylable-panel-drivers'
import { addStylableDirectiveRules, fixDisabledAttributeToPseudoStateInCss } from '@wix/stylable-viewer-flatten'
import _ from 'lodash'
import { getSpxValueResolver } from '@wix/thunderbolt-commons'
import { findFontsInCss } from '@wix/stylable-viewer-flatten/dist/fonts/fonts-stylable'

const isExperimentOpen = (value: boolean | string | undefined) =>
	(value && (value === true || value === 'true' || value === 'new')) || false

const getStylableCompFonts = (styleItem: Style) => {
	const stcss = getSTCSS(styleItem)
	return findFontsInCss(stcss, () => true, false)
}

const getSTCSS = (styleItem: Style): string =>
	_.get(styleItem, ['style', 'properties', '$st-css'], _.get(styleItem, ['style', 'properties', 'st-css'], ''))

const getStylableCompCss = (
	stylableEditorInstance: StylableEditor,
	namespace: string,
	styleItem: Style,
	isMobileView: boolean,
	experiments: Record<string, string | true>
	/* themeCounter: number*/
) => {
	const isScaleInStylableExperimentOpen = isExperimentOpen(experiments['specs.thunderbolt.stylableWithScaleSupport'])
	const componentStyle = getSTCSS(styleItem)
	const { spx } = styleItem
	const styleWithDirectives = getStyleWithDirectives({
		componentStyle,
		isMobileView,
		isScaleInStylableExperimentOpen,
		spx,
	})
	if (isScaleInStylableExperimentOpen && spx?.resolverType === 'scale') {
		/**
		 * Register a spx resolver that calculates the cqw value based on the spx refWidth of the styleItem
		 * this function will be used by stylable transformer to resolve the spx values in the user's stylesheet
		 */
		const spxImportPath = `wix-spx-${spx.refWidth}`
		stylableEditorInstance.stylableDriver.registerJsModule(spxImportPath, getSpxValueResolver(spx))
	}

	const meta = getTransformedMeta(stylableEditorInstance.stylableDriver, namespace, styleWithDirectives)
	return meta.outputAst!.toString()
}

const getStyleWithDirectives = ({
	componentStyle,
	isMobileView,
	isScaleInStylableExperimentOpen = true,
	spx,
	keepSpxUnit,
	usePanelThemeFonts,
	shouldNormalizeSpx,
}: {
	componentStyle: string
	isMobileView: boolean
	isScaleInStylableExperimentOpen?: boolean
	spx?: Spx
	keepSpxUnit?: boolean
	usePanelThemeFonts?: boolean
	shouldNormalizeSpx?: boolean
}): string => {
	return addStylableDirectiveRules(
		componentStyle,
		isMobileView,
		{
			breakpoints: true,
			siteColors: true,
			fontClasses: true,
			additionalJsModules: true,
			spxResolver: isScaleInStylableExperimentOpen,
		},
		{
			spx,
			keepSpxUnit,
			usePanelThemeFonts,
			shouldNormalizeSpx,
		}
	)
}

const getTransformedMeta = (
	stylableDriver: StylableDriver,
	namespace: string,
	componentStyle: string
): ReturnType<StylesheetDriver['getMeta']> => {
	stylableDriver.writeFile('/currently-transpiling.st.css', '') // TODO we can refactor this to not use writeFile

	const metaBefore = stylableDriver.stylable.fileProcessor.processContent(
		componentStyle,
		'/currently-transpiling.st.css'
	)
	metaBefore.namespace = namespace
	const { meta } = stylableDriver.stylable.transform(metaBefore)
	return meta
}

function updatePanelFsSideEffectForEditing(
	styleId: string,
	namespace: string,
	stylableDriver: StylableDriver,
	componentStyle: string,
	fixDisabledAttribute: boolean
) {
	const path = `/${styleId}.st.css`
	const fixedStyle = fixDisabledAttribute ? fixDisabledAttributeToPseudoStateInCss(componentStyle) : componentStyle // see https://jira.wixpress.com/browse/STYL-877
	stylableDriver.writeFile(path, fixedStyle)
	stylableDriver.getStylesheetMeta(path).namespace = namespace
}

const getStylableCompCssWithOverrides = (
	stylableEditorInstance: StylableEditor,
	namespace: string,
	styleItem: Style,
	isMobileView: boolean,
	experiments: Record<string, string | true>
) => {
	const componentStyle = getSTCSS(styleItem)
	const { spx } = styleItem
	const { stylableDriver } = stylableEditorInstance
	const fixDisabledAttribute = experiments['specs.thunderbolt.fixStylableDisabledAttribute'] === true

	const styleWithDirectives = getStyleWithDirectives({
		componentStyle,
		isMobileView,
		spx,
	})

	const panelStyle = getStyleWithDirectives({
		componentStyle,
		isMobileView,
		spx,
		/**
		 * In the stylable panel, we don't want to resolve the spx values to cqw,
		 * the panel should read and write in spx
		 */
		keepSpxUnit: true,
		usePanelThemeFonts: true,
		shouldNormalizeSpx: true,
	})

	if (spx?.resolverType === 'scale') {
		/**
		 * Register a spx resolver that calculates the cqw value based on the spx refWidth of the styleItem
		 * this function will be used by stylable transformer to resolve the spx values in the user's stylesheet
		 */
		const spxImportPath = `wix-spx-${spx.refWidth}`
		stylableEditorInstance.stylableDriver.registerJsModule(spxImportPath, getSpxValueResolver(spx))
	}

	// In editing time, fs needs to be updated for panel
	updatePanelFsSideEffectForEditing(styleItem.id, namespace, stylableDriver, panelStyle, fixDisabledAttribute)

	const meta = getTransformedMeta(stylableDriver, namespace, styleWithDirectives)
	stylableDriver.applyEditorTransformations(meta)
	return meta.outputAst!.toString()
}

const replaceSpxWithPx = (sizeUnit: string | undefined) => (sizeUnit === 'spx' ? 'px' : sizeUnit)

export {
	getStylableCompCss,
	getTransformedMeta,
	getStylableCompFonts,
	getSTCSS,
	getStyleWithDirectives,
	getStylableCompCssWithOverrides,
	replaceSpxWithPx,
}
